// @owners { team: patients }
import { Body, Column, H1, LgPadding, MdSpacing, SmSpacing, isFuzeRxBrand } from '@alto/design-system';
import { CourierTipInput } from '@alto/features';
import React, { useEffect, useRef } from 'react';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { type AnalyticsProps, SOURCES } from '~shared/features/courier-tips/AnalyticsProps';
import { COURIER_NEW_TIP_COPY, COURIER_TIP_COPY, INITIAL_TIP_AMOUNT } from '~shared/features/courier-tips/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ChoosePaymentMethod } from './ChoosePaymentMethod';
import TipQualifiedExpenseWarning from './TipQualifiedExpenseWarning';
import { Image } from '~web/components/Image';

type Props = {
  readonly analyticsProps: AnalyticsProps;
  readonly editTipAmount: (tip: number) => void;
  readonly editTipPaymentMethodID: (arg0: number) => void;
  readonly tipAmount: number;
  readonly tipPaymentMethodID: number | null | undefined;
};

export const EditOrAddTip = ({
  analyticsProps,
  editTipAmount,
  editTipPaymentMethodID,
  tipAmount,
  tipPaymentMethodID,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { source } = analyticsProps;
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    // leave tip as is if one is already present for the shipment
    if (!tipAmount && source !== SOURCES.EDIT_DELIVERY) {
      loaded.current = true;
      editTipAmount(INITIAL_TIP_AMOUNT);
    }
  }, [tipAmount, source, editTipAmount]);

  useEffect(() => {
    const event =
      source === SOURCES.POST_DELIVERY
        ? EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_VIEWED
        : EVENTS.COURIER_TIPS__EDIT_TIP_VIEWED;
    trackEvent({ event, params: analyticsProps, additionalFields: { tip_amount: tipAmount } });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LgPadding>
      <Column>
        {isFuzeRxBrand() ? null : (
          <Image
            src={getImage('bikeCourierWithBackground')}
            alt={IMG_ALT_TEXTS.bikeCourier}
          />
        )}
        <H1 center>{COURIER_TIP_COPY.CARD_HEADER}</H1>
        <MdSpacing />
        <Body center>{COURIER_NEW_TIP_COPY.CARD_BODY}</Body>
        <MdSpacing />
        <CourierTipInput
          handleEditTipAmount={editTipAmount}
          tipAmount={tipAmount}
          analyticsProps={analyticsProps}
        />
        <MdSpacing />
        <ChoosePaymentMethod
          paymentMethodID={tipPaymentMethodID}
          selectPaymentMethodID={editTipPaymentMethodID}
        />
        <SmSpacing />
        <TipQualifiedExpenseWarning />
      </Column>
    </LgPadding>
  );
};
